import { useEffect, useRef, useState } from 'react';

/**
 * [T149-PR1] Generic ResizeObserver hook.
 * [T149-PR3] Added requestAnimationFrame throttling (BH-6 mitigation).
 *
 * Tracks the contentRect of a target element via the native
 * ResizeObserver Web API
 * ({@link https://developer.mozilla.org/en-US/docs/Web/API/ResizeObserver}).
 *
 * Resize callbacks are coalesced into a single `requestAnimationFrame`
 * tick (per docs/design/T149-WIDGET-PARITY-SPEC.md → BH-6) so that
 * `react-grid-layout` drag operations firing >30 events/sec do not cause
 * layout thrash inside chart skins. A pending rAF is cancelled on unmount
 * so no callback fires after teardown.
 *
 * @returns
 *   - `ref`: attach to the element you want to measure
 *   - `size`: latest `{ width, height }` (both `0` until the first observation)
 *
 * Usage:
 * ```tsx
 * const { ref, size } = useResizeObserver<HTMLDivElement>();
 * return <div ref={ref}>{size.width}×{size.height}</div>;
 * ```
 *
 * Notes:
 * - No external dependency — uses the browser-native `ResizeObserver`.
 * - In test/SSR environments without `ResizeObserver`, the hook is a no-op
 *   (size stays `{ width: 0, height: 0 }`) and never throws.
 * - In environments without `requestAnimationFrame`, falls back to
 *   synchronous dispatch so SSR snapshots still work.
 */
export function useResizeObserver<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [size, setSize] = useState<{ width: number; height: number }>({
    width: 0,
    height: 0,
  });

  useEffect(() => {
    const node = ref.current;
    if (!node || typeof ResizeObserver === 'undefined') return;

    let pendingRaf: number | null = null;
    let pendingSize: { width: number; height: number } | null = null;
    const hasRaf =
      typeof window !== 'undefined' &&
      typeof window.requestAnimationFrame === 'function';

    const flush = () => {
      pendingRaf = null;
      const next = pendingSize;
      pendingSize = null;
      if (!next) return;
      setSize((prev) =>
        prev.width === next.width && prev.height === next.height ? prev : next,
      );
    };

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;
      pendingSize = { width, height };
      if (!hasRaf) {
        flush();
        return;
      }
      // Coalesce successive resize events into one rAF tick.
      if (pendingRaf != null) return;
      pendingRaf = window.requestAnimationFrame(flush);
    });

    observer.observe(node);
    return () => {
      observer.disconnect();
      if (pendingRaf != null && hasRaf) {
        window.cancelAnimationFrame(pendingRaf);
        pendingRaf = null;
      }
      pendingSize = null;
    };
  }, []);

  return { ref, size };
}

export default useResizeObserver;
